import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Grid, Grow } from '@mui/material';

export default function FeatureCard({ icon, title, description }) {
  const iconRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    const current = iconRef.current;
    if (current) {
      observer.observe(current);
    }

    return () => {
      if (current) {
        observer.unobserve(current);
      }
    };
  }, []);

  return (
    <Grid
      item
      xs={12}
      md={5}
      lg={4}
      sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        gap: 1,
        py: 2
      }}
    >
      {/* Contenedor del ícono con animaciones */}
      <Box 
        ref={iconRef}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '80px',
          height: '80px',
          perspective: '1000px'
        }}
      >
        <Grow in={visible} timeout={700}>
          <Box
            component="img"
            src={icon}
            alt={title}
            sx={{
              width: '48px',
              height: '48px',
              // Animación constante de flotación
              '@keyframes float': {
                '0%, 100%': {
                  transform: 'translateY(0)'
                },
                '50%': {
                  transform: 'translateY(-8px)'
                }
              },
              animation: 'float 3s ease-in-out infinite',
              // Efectos al hover
              transition: 'all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)',
              '&:hover': {
                animation: 'none', // Detiene la animación flotante
                transform: 'translateY(-12px) scale(1.2)',
                filter: 'drop-shadow(0 10px 15px rgba(212, 167, 106, 0.4))',
                cursor: 'pointer'
              },
              // Estilo normal
              filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2))'
            }}
          />
        </Grow>
      </Box>

      {/* Título con efecto sutil al hover del ícono */}
      <Grow in={visible} timeout={800}>
        <Typography
          sx={{
            position: 'relative',
            py: 0.3,
            px: 1.5,
            borderRadius: '50rem',
            background: 'linear-gradient(rgba(255,255,255,0.1), rgba(255,255,255,0.1))',
            fontSize: '18px',
            fontWeight: '600',
            display: 'inline-block',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            boxShadow: '0px 2px 10px rgba(0,0,0,0.5)',
            transition: 'all 0.3s ease',
            zIndex: 1,
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              padding: '1px',
              borderRadius: '50rem',
              background: 'linear-gradient(180deg, rgba(0,255,255,0.5), rgba(10, 98, 116, 0.3))',
              WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
              WebkitMaskComposite: 'xor',
              maskComposite: 'exclude',
              zIndex: -1
            }
          }}
        >
          {title}
        </Typography>
      </Grow>

      {/* Descripción */}
      <Grow in={visible} timeout={900}>
        <Typography sx={{ 
          fontSize: '14px', 
          maxWidth: '300px', 
          textAlign: 'justify',
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(20px)',
          transition: 'all 0.5s ease'
        }}>
          {description}
        </Typography>
      </Grow>
    </Grid>
  );
}